import { motion, useReducedMotion } from "motion/react";
import type { ComponentPropsWithoutRef } from "react";

import { EASE_OUT_STRONG } from "./constants";

interface AnimatedListItemProps
  extends ComponentPropsWithoutRef<typeof motion.div> {
  /** Position in list — drives stagger delay unless `delay` is provided */
  index?: number;
  /** Explicit delay in seconds. Overrides the index-based calculation */
  delay?: number;
  /** Per-index delay multiplier. Default: 0.05s */
  staggerDelay?: number;
}

/**
 * Independent list item with enter/exit animation for use inside AnimatePresence.
 * Handles prefers-reduced-motion internally — pass raw delay values without checks.
 */
export function AnimatedListItem({
  index = 0,
  delay,
  staggerDelay = 0.05,
  ...props
}: AnimatedListItemProps) {
  const shouldReduceMotion = useReducedMotion();
  const computedDelay = delay ?? index * staggerDelay;

  return (
    <motion.div
      initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: shouldReduceMotion ? 0 : 4 }}
      transition={{
        duration: shouldReduceMotion ? 0 : 0.2,
        ease: EASE_OUT_STRONG,
        delay: shouldReduceMotion ? 0 : computedDelay,
      }}
      {...props}
    />
  );
}
